import Table from 'common/table/table';
import { Icons, RowIconAction } from 'common/table/table-helper';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Outlet, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { Div, Icon, SubTitle } from 'ui';
import { withShadow } from 'ui/styles';
import { withRouter } from 'utils/withRouter';
import PartDetails from './part-details';
import PartListAttributes from './part-list-attributes';
import PartToolbar from './part-toolbar';

const Wrapper = styled(Div)`
  ${withShadow};
  border-radius: 4px;
  background-color: #fff;
  padding: 8px 13px 13px;
  margin-bottom: 20px;
`;

const StyledIcon = styled(Icon)`
  font-size: 15px;
  margin-right: 5px;
  --fa-secondary-color: #7e7e7e;
  --fa-primary-color: #6bc93c;
  --fa-secondary-opacity: 1;
  color: #7e7e7e;
`;

const Parts = ({
  data = [],
  intl,
  shipTo,
  history,
  orderId,
}) => {
  const [selectedPart, setSelectedPart] = useState(null);
  const [filter, setFilter] = useState('all');
  const decimalMask = useSelector(state => state.serviceCenter.isDecimal);

  const handleShowDetails = (part) => {
    setSelectedPart(part);
    history.push('part-details', { cancelBlock: true });
  };

  const getFilterOptions = () => [
    {
      text: intl.formatMessage({ id: 'order.parts.ALL' }),
      value: 'all',
    },
    {
      text: intl.formatMessage({ id: 'order.parts.NOT_DELIVERED' }),
      value: 'notDelivered',
    },
    {
      text: intl.formatMessage({ id: 'order.parts.DELIVERED' }),
      value: 'delivered',
    },
  ];

  const getColumns = () => [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '30px',
      renderFn: r => <Icons>
        <RowIconAction
          title={intl.formatMessage({ id: 'order.tooltip.viewPart' })}
          tooltipKey='eye'
          iconKey='eye'
          action={() => handleShowDetails(r)}
          disabled={false}
        />
      </Icons>,
    },
    {
      id: 'materialNumber',
      displayName: intl.formatMessage({ id: 'order.parts.MATERIAL_NO' }),
      fieldName: 'materialNumber',
      width: '120px',
    },
    {
      id: 'materialDescription',
      displayName: intl.formatMessage({ id: 'order.parts.MATERIAL_DESC' }),
      fieldName: 'materialDescription',
      tooltip: row => row.materialDescription,
      width: '230px',
    },
    {
      id: 'quantityOrdered',
      displayName: intl.formatMessage({ id: 'order.parts.QTY_ORDERED' }),
      fieldName: 'quantityOrdered',
      tooltip: row => <span>
        <FormattedNumber value={row.quantityOrdered} />
        {row.quantityOrderedUnit}
      </span>,
      width: '100px',
    },
    {
      id: 'cost',
      displayName: intl.formatMessage({ id: 'order.parts.COST' }),
      fieldName: 'cost',
      width: '100px',
      tooltip: (part) => {
        if (!Number.isFinite(part.cost)) {
          return null;
        }

        if (part.costCurrency) {
          const number = intl.formatNumber(part.cost, {
            style: 'currency', //eslint-disable-line
            currency: part.costCurrency,
          });
          if (!decimalMask) {
            return number.replace(/\.00$/, '').replace(/^(\D+)/, '$1 ');
          }
          return number.replace(/^(\D+)/, '$1 ');
        }
        return <FormattedNumber value={part.cost} />;
      },
      renderFn: (part) => {
        if (!Number.isFinite(part.cost)) {
          return null;
        }

        if (part.costCurrency) {
          const number = intl.formatNumber(part.cost, {
            style: 'currency', //eslint-disable-line
            currency: part.costCurrency,
          });
          if (!decimalMask) {
            return number.replace(/\.00$/, '').replace(/^(\D+)/, '$1 ');
          }
          return number.replace(/^(\D+)/, '$1 ');
        }
        return <FormattedNumber value={part.cost} />;
      },
    },
    {
      id: 'weight',
      displayName: intl.formatMessage({ id: 'order.parts.WEIGHT' }),
      fieldName: 'weight',
      width: '100px',
      tooltip: part => `${part?.weight || ''}${part?.weightUnit ? ` ${part.weightUnit}` : ''}`,
      renderFn: part => `${part?.weight || ''}${part?.weightUnit ? ` ${part.weightUnit}` : ''}`,
    },
    {
      id: 'vendorName',
      displayName: intl.formatMessage({ id: 'order.parts.VENDOR' }),
      fieldName: 'vendorName',
      tooltip: row => row.vendorName,
      width: '230px',
    },
    {
      id: 'logisticFlow',
      displayName: intl.formatMessage({ id: 'order.parts.LOGISTIC_FLOW' }),
      fieldName: 'logisticFlow',
      tooltip: row => row.logisticFlowName,
      width: '120px',
    },
    isToggleEnabled('logisticPlants') && {
      id: 'plant',
      displayName: intl.formatMessage({ id: 'order.parts.PLANT' }),
      fieldName: 'plant',
      width: '120px',
    },
    {
      id: 'poNumber',
      displayName: intl.formatMessage({ id: 'order.parts.PO_NO' }),
      fieldName: 'poNumber',
      width: '150px',
      tooltip: row => row.poNumber,
    },
    {
      id: 'LogisticsDate',
      displayName: intl.formatMessage({
        id: 'order.parts.LOGISTICS_DATE',
      }),
      fieldName: 'LogisticsDate',
      width: '250px',
      renderFn: part => <Div>
        <StyledIcon className={part?.logisticsDate?.icon} />
        {part?.logisticsDate?.fullDescription}
      </Div>,
    },
  ];

  const filterParts = (parts) => {
    switch (filter) {
      case 'notDelivered':
        return parts?.filter(p => !p.deliveries || !p.deliveries.length);
      case 'delivered':
        return parts?.filter(p => p.deliveries && p.deliveries.length);
      default:
        return parts;
    }
  };

  if (!data?.parts || !data?.parts?.length || Object.keys(data).length === 0) {
    return null;
  }

  const parts = filterParts(data.parts);

  return (
    <Div marginTop={45}>
      <SubTitle>
        <FormattedMessage id='common.titles.PARTS_TITLE_COUNT' values={{ count: parts.length }} />
        <PartToolbar
          filter={filter}
          filterOptions={getFilterOptions()}
          parts={parts}
          handleFilterChange={newFilter => setFilter(newFilter)}
        />
      </SubTitle>
      <Wrapper last>
        <PartListAttributes
          orderId={orderId}
          partList={data}
          disabled={false}
          shipTo={shipTo}
          isReadOnly={true}
        />
        <Table
          labelInColumn={true}
          columns={getColumns()}
          data={parts}
          totalDataCount={10}
          hasCustomHeader={true}
          fullWidth={true}
        />
      </Wrapper>
      <Routes>
        <Route
          path='part-details'
          element={<PartDetails part={selectedPart} onClose={() => setSelectedPart(null)} />}
        />
      </Routes>
      <Outlet />
    </Div>
  );
};

Parts.propTypes = {
  data: PropTypes.object,
  intl: PropTypes.object,
  shipTo: PropTypes.object,
  history: PropTypes.object,
  orderId: PropTypes.string,
};

export default injectIntl(withRouter(Parts));
